import { EventInteraction, useTracking } from '@eventbrite/ads';
import {
    CONSUMER_CARD_DEFAULTS,
    DiscoveryEvent,
    EventCard,
    EventClient,
    EventClientProvider,
    FormattedEvent,
    transformDiscoveryEvent,
} from '@eventbrite/event-renderer';
import { gettext } from '@eventbrite/i18n';
import { getLayer, logEvent } from '@eventbrite/statsig';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import {
    LISTING_HEAP_PAGE_AREA_IDS,
    STATSIG_EVENT_NAMES,
} from '../../constants';
import { CategoryBrowseHorizontalSlider } from '../../containers/CategoryBrowseHorizontalSlider';
import { TrackingContext } from '../../context';
import { AffiliateCodes } from '../../types';
import { Location } from '../../types/index';
import { ShareProps } from '../ShareEvent/ShareEventRoot';
import { getSortedEvents } from '../_shared/getSortedEvents';
import { SimplifiedOrganizerInfo } from '../_shared/OrganizerInfo/SimplifiedOrganizerInfo';
import { StyleTypes } from '../_shared/OrganizerStats/OrganizerStats';
import { EnhancedExpiredEventsBadge } from './EnhancedExpiredEventsBadge';
import { EnhancedExpiredEventsList } from './EnhancedExpiredEventsList';
import './ExpiredEvents.scss';
import { usePromotedEvents } from './usePromotedEvents';

export type ExpiredEventsPageProps = {
    relatedEvents: DiscoveryEvent[];
    eventName: string;
    eventLocation: Location;
    organizer: {
        name: string;
        profilePicture?: string;
        id: string;
    };
    isAuthenticated: boolean;
    shareComponentProps: ShareProps;
    onViewDetailsClick: () => void;
    isSalesEndedListingExperimentEnabled: boolean;
    locale: string;
    isExpiredEvent: boolean;
    launchLinkToCreatorCollectionsOnListing: boolean;
    scrollToRelatedEvents: boolean;
    isSalesEnded: boolean;
    salesMessage: string;
};

export const ExpiredEventsPage = ({
    relatedEvents,
    eventName,
    eventLocation,
    organizer,
    isAuthenticated,
    shareComponentProps,
    onViewDetailsClick,
    locale,
    isSalesEndedListingExperimentEnabled,
    isExpiredEvent,
    launchLinkToCreatorCollectionsOnListing,
    scrollToRelatedEvents,
    isSalesEnded,
    salesMessage,
}: ExpiredEventsPageProps) => {
    const {
        name: organizerName,
        profilePicture: organizerAvatar,
        id: organizerId,
    } = organizer;

    const [showRelatedCategories, setShowRelatedCategories] = useState(false);

    const { promotedEvents, loaded } = usePromotedEvents();

    useEffect(() => {
        const variant = getLayer(
            'listing_page_experiments',
            'category_secondary_nav',
            'hide',
        );
        setShowRelatedCategories(variant == 'show');
    }, []);

    const eventsListWrapper = isSalesEndedListingExperimentEnabled ? (
        <EnhancedExpiredEventsList
            {...{
                isAuthenticated,
                locale,
                affCode: AffiliateCodes.AFF_RELATED_EVENTS_EXPIRED,
                organizer,
                launchLinkToCreatorCollectionsOnListing,
                isExpiredEvent,
                isSalesEndedListingExperimentEnabled,
                scrollToRelatedEvents,
            }}
        />
    ) : (
        <ExpiredEventsList
            {...{
                relatedEvents,
                promotedEvents,
                isAuthenticated,
                shareComponentProps,
                promotedEventsLoaded: loaded,
                isSalesEnded,
            }}
        />
    );

    return (
        <div className="expired-event">
            <div className="expired-wrapper">
                <div className="container">
                    {showRelatedCategories && (
                        <div className="related-categories">
                            <CategoryBrowseHorizontalSlider
                                {...eventLocation}
                            />
                            <hr className="expired-divider" />
                        </div>
                    )}

                    <div>
                        {isSalesEndedListingExperimentEnabled ? (
                            <EnhancedExpiredEventsBadge
                                onViewDetailsClick={onViewDetailsClick}
                                isEventDetailsPage={false}
                                salesMessage={salesMessage}
                            />
                        ) : (
                            <span className="expired-badge">
                                {gettext('Event ended')}
                            </span>
                        )}
                    </div>

                    <div className="expired-info">
                        <h1 className="expired-heading">{eventName}</h1>
                    </div>
                    {!isSalesEndedListingExperimentEnabled ? (
                        <button
                            className="expired-view-details"
                            onClick={onViewDetailsClick}
                        >
                            {gettext('View details')}
                        </button>
                    ) : null}
                    <hr className="expired-divider" />
                    <div>
                        <h2 className="expired-organizer-intro">
                            {gettext(
                                'Follow this organizer to stay informed on future events',
                            )}
                        </h2>
                        <div className="expired-organizer">
                            <SimplifiedOrganizerInfo
                                organizerName={organizerName}
                                organizerAvatar={organizerAvatar}
                                organizerId={organizerId}
                                style={StyleTypes.CONDENSED}
                                pageArea={
                                    LISTING_HEAP_PAGE_AREA_IDS.EXPIRED_EVENTS_PAGE
                                }
                            />
                        </div>
                    </div>
                </div>
            </div>

            {eventsListWrapper}
        </div>
    );
};

const ExpiredEventsList = ({
    relatedEvents,
    promotedEvents,
    isAuthenticated,
    shareComponentProps,
    promotedEventsLoaded,
    isSalesEnded,
}: {
    relatedEvents: DiscoveryEvent[];
    promotedEvents: FormattedEvent[];
    isAuthenticated: boolean;
    shareComponentProps: ShareProps;
    promotedEventsLoaded: boolean;
    isSalesEnded: boolean;
}): JSX.Element => {
    const affCode = AffiliateCodes.AFF_RELATED_EVENTS_EXPIRED;
    const eventClient = new EventClient();

    const allEvents = useMemo(
        () => getSortedEvents(relatedEvents as any, promotedEvents),
        [promotedEvents, relatedEvents],
    );

    const eventCards = (
        <div className="expired-wrapper expired-wrapper--bg-gray">
            <div className="container">
                <div>
                    <h2 className="related-events__heading">
                        {gettext('Events you might like')}
                    </h2>
                </div>
                <ul className="expired-events-grid">
                    <EventClientProvider client={eventClient}>
                        {allEvents.map((event: any, i: number) => {
                            const shareEventProps = {
                                eventId: event.id,
                                eventName: event.name,
                                eventUrl: event.url,
                                ...shareComponentProps,
                            };
                            return (
                                <li
                                    key={i}
                                    className="expired-events-grid__item"
                                >
                                    <EventCard
                                        {...CONSUMER_CARD_DEFAULTS}
                                        eventData={
                                            event.isPromoted
                                                ? event
                                                : transformDiscoveryEvent(event)
                                        }
                                        heapLocationString={
                                            event.isPromoted
                                                ? 'Related Events - Event Expired Page - Promoted'
                                                : undefined
                                        }
                                        affCode={affCode}
                                        key={event.id}
                                        gaCategory="expired-events"
                                        isAuthenticated={isAuthenticated}
                                        shareOptions={shareEventProps}
                                        onWillShareClick={() =>
                                            isSalesEnded &&
                                            logEvent(
                                                STATSIG_EVENT_NAMES.LISTING_RELATED_EVENT_SHARE,
                                            )
                                        }
                                        onClick={() =>
                                            isSalesEnded &&
                                            logEvent(
                                                STATSIG_EVENT_NAMES.LISTING_RELATED_EVENT_CLICK,
                                            )
                                        }
                                        onSave={(saved) =>
                                            isSalesEnded &&
                                            // bug exists where saved doesn't update before it is passed, so checking for opposite of true
                                            !saved &&
                                            logEvent(
                                                STATSIG_EVENT_NAMES.LISTING_RELATED_EVENT_LIKE,
                                            )
                                        }
                                    />
                                </li>
                            );
                        })}
                    </EventClientProvider>
                </ul>
            </div>
        </div>
    );

    return promotedEventsLoaded ? (
        <Trackable events={allEvents}>{eventCards}</Trackable>
    ) : (
        eventCards
    );
};

export const Trackable: React.FC<{ events: FormattedEvent[] }> = ({
    children,
    events,
}) => {
    const context = useContext(TrackingContext);

    const { ref } = useTracking<HTMLDivElement>(({ id, event }) =>
        aggregateContext({
            id,
            event,
            context,
            events,
        }),
    );

    return <div ref={ref}>{children}</div>;
};

const RELATED_EVENTS_PLACEMENT_NAME = 'related_events';
const EXPIRED_EVENTS_PLACEMENT_NAME = 'event_expired_page';

function aggregateContext({
    id,
    context,
    events,
    event: eventData,
}: Omit<EventInteraction, 'action'> & { context: any; events: any }) {
    const { event, absolutePosition } = getEventFromId(
        events,
        id,
        eventData.isPromoted,
    );
    return {
        user: {
            id: context.userId,
            locale: context.locale,
            guestId: context.guestId,
            sessionId: context.sessionId,
        },
        adId: event?.promotedListingMetadata?.adId,
        place: {
            name: RELATED_EVENTS_PLACEMENT_NAME,
            position: absolutePosition,
            page: 1,
            context: {
                related_event_id: context.event?.id,
                is_online: context.event?.isOnline,
                place_id: context.event?.localityPlaceId,
            },
            subInterface: {
                name: EXPIRED_EVENTS_PLACEMENT_NAME,
                position: '',
            },
            placementId: event?.promotedListingMetadata?.placementId,
        },
        venueId: event?.venue?.id,
    };
}

function getEventFromId(
    allEvents: DiscoveryEvent[],
    eventIdToFind: string,
    isPromoted: boolean,
) {
    const promotedEvents = allEvents.filter(
        (event: DiscoveryEvent) => event?.promotedListingMetadata,
    );
    const organicEvents = allEvents.filter(
        (event: DiscoveryEvent) => !event?.promotedListingMetadata,
    );
    if (isPromoted) {
        const eventPosition = promotedEvents.findIndex(
            (event) => event?.id === eventIdToFind,
        );
        return {
            event: promotedEvents[eventPosition],
            absolutePosition: eventPosition + 1,
        };
    }
    const eventPosition = organicEvents.findIndex(
        (event) => event?.id === eventIdToFind,
    );
    return {
        event: organicEvents[eventPosition],
        absolutePosition: eventPosition + 1,
    };
}
